import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl, API_ENDPOINTS } from '../../config/api';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();



  const handleGoogleLogin = useCallback(async (credentialResponse) => {
    try {
      const response = await axios.post(getApiUrl(API_ENDPOINTS.googleLogin), {
        credential: credentialResponse.credential
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Navigate based on subdomain
      const isAdminSubdomain = window.location.hostname === 'admin.fishmanhealth.com' || 
                              window.location.hostname === 'admin.fishmanmanhealth.com';
      navigate(isAdminSubdomain ? '/dashboard' : '/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Google login failed');
    }
  }, [navigate]);

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('token');
    if (token) {
      // Navigate to dashboard (different path based on subdomain)
      const isAdminSubdomain = window.location.hostname === 'admin.fishmanhealth.com' || 
                              window.location.hostname === 'admin.fishmanmanhealth.com';
      navigate(isAdminSubdomain ? '/dashboard' : '/admin/dashboard');
    }

    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleLogin
        });

        window.google.accounts.id.renderButton(
          document.getElementById('googleSignInButton'),
          { theme: 'outline', size: 'large', width: '100%' }
        );
      }
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [navigate, handleGoogleLogin]);

  const handleManualLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('Attempting login with:', { email });

    try {
      const response = await axios.post(getApiUrl(API_ENDPOINTS.login), {
        email,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: false
      });

      console.log('Login successful:', response.data);

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Navigate based on subdomain - always go to dashboard
      const isAdminSubdomain = window.location.hostname === 'admin.fishmanhealth.com' || 
                              window.location.hostname === 'admin.fishmanmanhealth.com';
      
      navigate(isAdminSubdomain ? '/dashboard' : '/admin/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      
      let errorMessage = 'Login failed';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="login-container">
      <div className="login-box">
        <div className="logo-section">
          <img src="/Fish_Logo.jpeg" alt="Fishman Health" className="logo" />
          <h1>Admin Dashboard</h1>
          <p>Secure access for authorized personnel only</p>
        </div>
        
        <form onSubmit={handleManualLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button type="submit" disabled={loading} className="login-button">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <div className="divider">
          <span>OR</span>
        </div>
        
        <div className="google-login">
          <div id="googleSignInButton"></div>
        </div>
        
        <div className="footer-text">
          <p>For technical support, contact your system administrator</p>
        </div>
      </div>
    </div>
  );
};

export default Login; 